import { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, ShoppingCart, Percent } from 'lucide-react';
import StatsCard from '../components/StatsCard';
import LeaderboardTable from '../components/LeaderboardTable';
import PerformanceChart from '../components/PerformanceChart';

interface SalesStats {
  totalValue: string;
  totalCommission: string;
  totalCount: number;
  avgTicket: string;
  sellerSales: {
    [key: string]: {
      count: number;
      value: number;
      commission: number;
    };
  };
  salesByDay: {
    [key: string]: {
      count: number;
      value: number;
    };
  };
}

export default function EstatisticasVendas() {
  const [stats, setStats] = useState<SalesStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadStats();
  }, [filter]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const params = new URLSearchParams();
      if (filter.start_date) params.append('start_date', filter.start_date);
      if (filter.end_date) params.append('end_date', filter.end_date);

      const res = await fetch(`${apiUrl}/api/stats/sales?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas de vendas:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  // Preparar dados para o gráfico
  const chartData = Object.entries(stats?.salesByDay || {})
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
    .map(([date, data]) => ({
      date: new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      value: data.value,
      count: data.count
    }));

  // Preparar dados para o ranking
  const sellerData = Object.entries(stats?.sellerSales || {}).map(([name, data], index) => ({
    id: `seller-${index}`,
    name,
    value: data.value,
    subtitle: `${data.count} vendas • R$ ${data.commission.toFixed(2)} comissão`
  }));

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Estatísticas de Vendas</h1>
        <p className="text-gray-600">Performance de vendas e comissões</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Filtrar Período</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              type="date"
              value={filter.start_date}
              onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={filter.end_date}
              onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilter({
                start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                end_date: new Date().toISOString().split('T')[0]
              })}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Últimos 30 dias
            </button>
          </div>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatsCard
          title="Valor Total"
          value={`R$ ${stats?.totalValue || '0.00'}`}
          icon={DollarSign}
          iconColor="text-emerald-500"
        />
        
        <StatsCard
          title="Total de Vendas"
          value={stats?.totalCount || 0}
          icon={ShoppingCart}
          iconColor="text-blue-500"
        />
        
        <StatsCard
          title="Ticket Médio"
          value={`R$ ${stats?.avgTicket || '0.00'}`}
          icon={TrendingUp}
          iconColor="text-orange-500"
        />
        
        <StatsCard
          title="Comissões"
          value={`R$ ${stats?.totalCommission || '0.00'}`}
          icon={Percent}
          iconColor="text-purple-500"
        />
      </div>

      {/* Gráfico de Vendas por Dia */}
      <div className="mb-6">
        <PerformanceChart
          data={chartData}
          title="Vendas por Dia"
          dataKey="value"
          color="#10b981"
        />
      </div>

      {/* Ranking de Vendedores */}
      <LeaderboardTable
        title="Ranking de Vendedores"
        data={sellerData}
        valueLabel="Valor Total"
        valueFormatter={(value) => `R$ ${value.toFixed(2)}`}
      />
    </div>
  );
}
